/**
 * The image refs this CLI defaults to, GENERATED from installer/release-pin.json
 * at build time — never hand-typed here.
 *
 * WHY THIS FILE EXISTS. Three CLI literals used to mirror the pin: the installer
 * tag `m8t bootstrap launch` pulls, and the coding-agent / azure-executor tags the
 * two `deploy` commands pull. A guard (scripts/check-release-pin.mjs)
 * compared them to the pin on every CI run — five copies of one fact, policed
 * rather than deduplicated. Since the versioning flag day the numbers are assigned
 * by CD (docs/versioning.md), so a hand-typed tag has no author who could keep it
 * right. Phase 2 of that epic deletes the copies: the pin declares the selection,
 * and `apps/cli/tsup.config.ts` bakes it in via esbuild `define`.
 *
 * `apps/cli/vitest.config.ts` injects the SAME values from the SAME helper
 * (scripts/pin-image-defaults.mjs), so the values a test asserts are the values a
 * published binary carries.
 *
 * DEV (`pnpm dev`, plain `tsx`) has no define step, so the constants fall back to
 * a `v0.0.0-dev` sentinel. That is deliberate and loud: a dev CLI that tries to
 * launch resolves nothing at the registry and fails naming the sentinel tag, which
 * is a better answer than silently pulling some plausible-looking real image. Pass
 * `--installer-tag` / `--image-tag` explicitly when driving a dev build against a
 * live registry.
 */

export interface PinnedImage {
  /** Full pullable ref, `<registry>/<image>:<tag>`. */
  readonly ref: string;
  /** Registry prefix, e.g. `ghcr.io/m8t-labs` — used to expand a bare `--image`. */
  readonly registry: string;
  /** Bare image name, e.g. `m8t-coding-agent`. */
  readonly image: string;
  /** Concrete `v<X.Y.Z>` tag. */
  readonly tag: string;
}

/**
 * Parse one injected component, or fall back to the dev sentinel.
 *
 * A malformed blob is treated as absent rather than thrown on: this module is
 * imported by the CLI's entry path, and throwing at import time would take down
 * `m8t --help` and `m8t version` too — the two commands a founder reaches for when
 * something is already wrong.
 */
function fromBuild(raw: string | undefined, fallbackRef: string): PinnedImage {
  if (raw) {
    try {
      const p = JSON.parse(raw) as Partial<PinnedImage>;
      if (p.ref && p.registry && p.image && p.tag) {
        return { ref: p.ref, registry: p.registry, image: p.image, tag: p.tag };
      }
    } catch {
      // fall through to the sentinel
    }
  }
  const at = fallbackRef.lastIndexOf("/");
  return {
    ref: `${fallbackRef}:${DEV_TAG}`,
    registry: fallbackRef.slice(0, at),
    image: fallbackRef.slice(at + 1),
    tag: DEV_TAG,
  };
}

/** Sentinel tag a non-built (tsx/dev) CLI carries. Mirrors CLI_VERSION's `0.0.0-dev`. */
export const DEV_TAG = "v0.0.0-dev";

// PUBLIC org (m8t-labs). A regression to the private `m8t-run` org shipped a broken
// npm CLI that pulled an inaccessible installer image — still guarded by a unit test.
// These bare refs are only the DEV fallback's shape; the real refs come from the pin.
export const PINNED_INSTALLER: PinnedImage = fromBuild(process.env.M8T_PINNED_INSTALLER, "ghcr.io/m8t-labs/m8t-installer");
export const PINNED_CODING_AGENT: PinnedImage = fromBuild(process.env.M8T_PINNED_CODING_AGENT, "ghcr.io/m8t-labs/m8t-coding-agent");
export const PINNED_AZURE_EXECUTOR: PinnedImage = fromBuild(process.env.M8T_PINNED_AZURE_EXECUTOR, "ghcr.io/m8t-labs/m8t-azure-executor");

/**
 * The platform release this CLI belongs to, from the SAME pin as the refs above it.
 *
 * `m8t bootstrap launch` passes it to the installer, which stamps and reports it
 * instead of the version baked into the engine image. That baked value is one
 * declaration behind by construction — the engine a release selects was built
 * before the release was declared — so the launcher is the only party that knows
 * the truth at install time. See installer/lib.sh's platform_version_resolved.
 *
 * Empty string on a non-built (tsx/dev) CLI, which is deliberate: a dev CLI has
 * no release to claim, and the installer's baked fallback is a better answer than
 * a made-up version. The `v0.0.0-dev` sentinel the image refs use would be worse
 * here — it is a well-formed version that would stamp cleanly and mean nothing.
 */
export const PINNED_PLATFORM_VERSION: string = (() => {
  // Shape-checked here too, not only at the two places that consume it. In a
  // non-built (tsx) CLI this is a REAL environment read, and the value is pushed
  // unquoted into an `az container create --environment-variables` list where a
  // space would split it into a second variable. The engine re-validates and the
  // dev gate makes it practically unreachable — but "safe because of a contract
  // kept elsewhere" is the thing this branch keeps finding and removing.
  const raw = process.env.M8T_PINNED_PLATFORM_VERSION ?? "";
  return /^\d+\.\d+\.\d+$/.test(raw) ? raw : "";
})();
